import { create } from 'zustand';
import type { AiCreditsState } from '../types/aiCredits';
import { getUserCreditsData, updateUserCredits } from '../utils/supabase';
import { useAuthStore } from './authStore';

export const useAiCreditsStore = create<AiCreditsState>()(
  (set, get) => ({
    planCredits: 0, // 初期値は 0（表示をリトライ後に更新）
    maxPlanCredits: 0,
    chargedCredits: 0,
    maxChargedCredits: 0,
    monthlyUsage: 0,
    usageHistory: [],
    lastResetDate: new Date().toISOString(),
    isLoading: false,
    isFetchingCredits: false,

    loadCredits: async () => {
      // 重複フェッチを防止
      if (get().isFetchingCredits) {
        console.log('AI credits fetch already in progress, skipping...');
        return;
      }

      const user = useAuthStore.getState().user;
      if (!user?.id) return;

      set({ isLoading: true, isFetchingCredits: true });

      try {
        // 最大 3 回リトライ（0.5 秒ごと）
        let creditsData = null;
        for (let i = 0; i < 3; i++) {
          creditsData = await getUserCreditsData(user.id);
          if (creditsData) break;
          await new Promise(r => setTimeout(r, 500));
        }

        if (creditsData) {
          set({
            planCredits: creditsData.plan_credits,
            maxPlanCredits: creditsData.max_plan_credits,
            chargedCredits: creditsData.charged_credits,
            monthlyUsage: creditsData.monthly_usage || 0,
            lastResetDate: creditsData.last_reset_date,
            isLoading: false,
            isFetchingCredits: false
          });
        } else {
          // レコードがまだ無い場合：初期値として10を使用
          set({
            planCredits: 10,
            maxPlanCredits: 10,
            chargedCredits: 0,
            monthlyUsage: 0,
            lastResetDate: new Date().toISOString(),
            isLoading: false,
            isFetchingCredits: false
          });
        }
      } catch (error) {
        console.error('Failed to load AI credits:', error);
        // エラー時も fallback 値で
        set({
          planCredits: 10,
          maxPlanCredits: 10,
          chargedCredits: 0,
          monthlyUsage: 0,
          lastResetDate: new Date().toISOString(),
          isLoading: false,
          isFetchingCredits: false
        });
      } finally {
        // 確実にフェッチフラグをリセット
        set(state => ({ ...state, isFetchingCredits: false }));
      }
    },

    useCredit: async () => {
      const { planCredits, chargedCredits } = get();
      const user = useAuthStore.getState().user;
      if (!user?.id) {
        throw new Error('ユーザー認証が必要です');
      }

      if (planCredits <= 0 && chargedCredits <= 0) {
        throw new Error('AIクレジットが不足しています');
      }

      if (planCredits <= 0 && chargedCredits > 0) {
        try {
          await updateUserCredits(user.id, {
            charged_credits: chargedCredits - 1
          });

          set(state => ({
            chargedCredits: state.chargedCredits - 1,
            usageHistory: [
              ...state.usageHistory,
              { timestamp: new Date().toISOString(), type: 'charged' }
            ]
          }));
        } catch (error) {
          console.error('Failed to update charged credits:', error);
          throw new Error('クレジットの更新に失敗しました');
        }
      } else {
        try {
          await updateUserCredits(user.id, {
            plan_credits: planCredits - 1
          });

          set(state => ({
            planCredits: state.planCredits - 1,
            usageHistory: [
              ...state.usageHistory,
              { timestamp: new Date().toISOString(), type: 'plan' }
            ]
          }));
        } catch (error) {
          console.error('Failed to update plan credits:', error);
          throw new Error('クレジットの更新に失敗しました');
        }
      }
    },

    addChargedCredits: (amount: number) => {
      const user = useAuthStore.getState().user;
      if (!user?.id) return;

      const { chargedCredits } = get();
      const newChargedCredits = chargedCredits + amount;

      updateUserCredits(user.id, {
        charged_credits: newChargedCredits
      }).then(() => {
        set(state => ({
          chargedCredits: newChargedCredits,
          maxChargedCredits: state.maxChargedCredits + amount
        }));
      }).catch(error => {
        console.error('Failed to add charged credits:', error);
      });
    },

    setMaxPlanCredits: (amount: number) => {
      const user = useAuthStore.getState().user;
      if (!user?.id) return;

      updateUserCredits(user.id, {
        max_plan_credits: amount
      }).then(() => {
        set({ maxPlanCredits: amount });
      }).catch(error => {
        console.error('Failed to set max plan credits:', error);
      });
    },

    resetPlanCredits: () => {
      const user = useAuthStore.getState().user;
      if (!user?.id) return;

      const { maxPlanCredits } = get();
      const now = new Date().toISOString();

      updateUserCredits(user.id, {
        plan_credits: maxPlanCredits,
        last_reset_date: now
      }).then(() => {
        set({
          planCredits: maxPlanCredits,
          lastResetDate: now
        });
      }).catch(error => {
        console.error('Failed to reset plan credits:', error);
      });
    },

    addChargedCreditsForUser: async (userId: string, amount: number) => {
      try {
        const creditsData = await getUserCreditsData(userId);
        if (!creditsData) {
          throw new Error('User credits data not found');
        }

        const newChargedCredits = (creditsData.charged_credits || 0) + amount;

        await updateUserCredits(userId, {
          charged_credits: newChargedCredits
        });

      } catch (error) {
        console.error('Failed to add charged credits for user:', error);
        throw error;
      }
    },

    setPlanCreditsForUser: async (userId: string, newCredits: number) => {
      try {
        await updateUserCredits(userId, {
          plan_credits: newCredits
        });
      } catch (error) {
        console.error('Failed to set plan credits for user:', error);
        throw error;
      }
    }
  })
);